import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { v4 as uuidv4 } from 'uuid';
import './userVerify.css';

const UserVerify = () => {
  // --- STATE MANAGEMENT ---
  const [userId] = useState(uuidv4());
  const [idvToken, setIdvToken] = useState('');
  const [magicLink, setMagicLink] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('IDLE'); 
  const [kycData, setKycData] = useState(null);
  const [searchToken, setSearchToken] = useState('');

  // --- NEW USER-CENTRIC PROFILE STATE ---
  const [userProfile, setUserProfile] = useState({
    trustScore: 45,
    tier: 'Tier 1 (Unverified)',
    linkedWallets: 1,
    lastActive: new Date().toLocaleDateString(),
    vault: [
      { type: 'Email Address', status: 'verified', date: 'Oct 12, 2025' },
      { type: 'Phone Number', status: 'verified', date: 'Oct 12, 2025' },
      { type: 'Government ID', status: 'missing', date: '---' }
    ]
  });

  // --- AUTO-RESUME LOGIC ON MOUNT ---
  useEffect(() => {
    console.log(`🚀 [DASHBOARD MOUNT] Component initialized. Generated User UUID: ${userId}`);
    
    const savedToken = localStorage.getItem('active_verifier_token');
    if (savedToken) {
      console.log(`💾 [SESSION RESTORE] Found active token ${savedToken}. Resuming session automatically.`);
      setIdvToken(savedToken);
      setVerificationStatus('PENDING'); 
      
      // Update local profile UI to show we are waiting for the ID
      updateProfilePending();
    }
  }, [userId]);

  const updateProfilePending = () => {
    setUserProfile(prev => ({
      ...prev,
      vault: prev.vault.map(item => item.type === 'Government ID' ? { ...item, status: 'pending' } : item)
    }));
  };

  const updateProfileSuccess = () => {
    setUserProfile(prev => ({
      ...prev,
      trustScore: 98,
      tier: 'Tier 2 (Verified)',
      vault: prev.vault.map(item => item.type === 'Government ID' ? { ...item, status: 'verified', date: new Date().toLocaleDateString() } : item)
    }));
  };

  // --- API & WEBHOOK LOGIC ---
  useEffect(() => {
    let intervalId;

    if (verificationStatus === 'PENDING' && idvToken) {
      console.log(`⏳ [POLLING INIT] Starting status checks for Token: ${idvToken} every 3 seconds...`);
      
      intervalId = setInterval(async () => {
        try {
          const response = await axios.get(`https://magiclink.myearth.id/api/verifier/session-status/${idvToken}`);
          
          if (response.data.status === 'COMPLETED') {
            clearInterval(intervalId);
            setVerificationStatus('COMPLETED');
            
            const payload = response.data.storedWebhookData || {};
            
            const newVerifiedUser = {
              userId: payload.userId || "Unknown",
              documentName: payload.documentName || "Unknown Credential",
              category: payload.category || "Data",
              fileData: payload.fileData, 
              publicKey: payload.publicKey || "UNKNOWN_PUBLIC_KEY", 
              verifiedAt: response.data.updatedAt,
              status: "APPROVED"
            };
            
            setKycData(newVerifiedUser);
            updateProfileSuccess();

            if (!kycData) {
              Swal.fire({
                icon: 'success',
                title: 'Credential Received!',
                text: `Successfully received ${newVerifiedUser.documentName} from the wallet.`,
                confirmButtonColor: '#2ea3f2'
              });
            }
            
            localStorage.removeItem('active_verifier_token');
          }
        } catch (error) {
          console.warn(`⚠️ [POLLING WAIT/ERROR] Backend check failed:`, error.message);
        }
      }, 3000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [verificationStatus, idvToken, userId, kycData]);

  const handleSearchPrevious = async () => {
    if (!searchToken.trim()) return;
    try {
      Swal.fire({ title: 'Searching Vault...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const response = await axios.get(`https://magiclink.myearth.id/api/verifier/session-status/${searchToken}`);
      
      if (response.data.status === 'COMPLETED') {
        const payload = response.data.storedWebhookData || {};
        setKycData({
          userId: payload.userId || "Unknown",
          documentName: payload.documentName || "Unknown Credential",
          category: payload.category || "Data",
          fileData: payload.fileData,
          publicKey: payload.publicKey || "UNKNOWN_PUBLIC_KEY", 
          verifiedAt: response.data.updatedAt,
          status: "APPROVED"
        });
        setIdvToken(searchToken);
        setVerificationStatus('COMPLETED');
        setSearchToken('');
        updateProfileSuccess();
        
        Swal.fire({ icon: 'success', title: 'Record Found!', text: 'Loaded historical payload.', confirmButtonColor: '#2ea3f2' });
      } else {
        Swal.fire({ icon: 'info', title: 'Pending', text: 'Session exists but data not yet shared.', confirmButtonColor: '#2ea3f2' });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Not Found', text: `Could not find token: ${searchToken}`, confirmButtonColor: '#2ea3f2' });
    }
  };

  const handleInitiateKYC = async () => {
    try {
      Swal.fire({ title: 'Generating Secure Link...', text: 'Requesting IDV Code...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

      const earthIdResponse = await axios.post(
        'https://activate.myearth.id/generate-idv-code',
        { user_id: userId },
        { headers: { 'Content-Type': 'application/json', 'client-id': 'IDV-Client-ImpactoV', 'x-api-key': 'VfiIrnOFP99h3nBkVyG1t8e6DC4bcyMnafXC1Y4Z' } }
      );

      const generatedIdvToken = earthIdResponse.data.code;
      setIdvToken(generatedIdvToken);
      
      localStorage.setItem('active_verifier_token', generatedIdvToken);

      Swal.update({ text: 'Compiling Space Wallet Magic Link...' });

      const payload = {
        verifier_name: "CryptoEx Verification",
        userid: userId,
        idv_token: generatedIdvToken, 
        requestType: "IDV",
        webhook_url: "https://magiclink.myearth.id/api/verifier/mock-webhook",
        email: "johndoe48@yopmail.com",
        dialCode: "+91",      
        phone: "8668970945",
        firstName: "John",
        lastName: "Doe"
      };

      const result = await axios.post('https://magiclink.myearth.id/api/verifier/generate-magic-link', payload);
      
      setMagicLink(result.data.magicLink);
      setVerificationStatus('PENDING');
      updateProfilePending();

      Swal.fire({ icon: 'success', title: 'Session Created', text: 'Magic link generated successfully.', confirmButtonColor: '#2ea3f2' });
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Connection Failed', text: 'Could not generate the magic link.', confirmButtonColor: '#2ea3f2' });
    }
  };

  const resetFlow = () => {
    setVerificationStatus('IDLE');
    setMagicLink('');
    setKycData(null);
    setIdvToken('');
    setUserProfile({
      trustScore: 45, tier: 'Tier 1 (Unverified)', linkedWallets: 1, lastActive: new Date().toLocaleDateString(),
      vault: [
        { type: 'Email Address', status: 'verified', date: 'Oct 12, 2025' },
        { type: 'Phone Number', status: 'verified', date: 'Oct 12, 2025' },
        { type: 'Government ID', status: 'missing', date: '---' }
      ]
    });
    localStorage.removeItem('active_verifier_token');
  };

  const handleViewDocument = () => {
    if (!kycData || !kycData.fileData) return;
    const isJson = kycData.documentName.includes('.json') || kycData.category === 'Identification'; 
    if (isJson) {
        try {
            const cleanBase64 = kycData.fileData.includes("base64,") ? kycData.fileData.split("base64,")[1] : kycData.fileData;
            const decodedJson = atob(cleanBase64);
            const parsedObj = JSON.parse(decodedJson);
            const newWindow = window.open();
            newWindow.document.write(`<pre>${JSON.stringify(parsedObj, null, 2)}</pre>`);
        } catch (e) { console.error("Could not parse JSON VC", e); }
    } else {
        fetch(kycData.fileData).then(res => res.blob()).then(blob => window.open(URL.createObjectURL(blob), '_blank'));
    }
  };

  const verifyIDV = async (credential, userPublicKey) => {
    const ssiBaseUrl = "https://ssi-test.myearth.id/api";
    const authorizationKey = "01a41742-aa8e-4dd6-8c71-d577ac7d463c";

    try {
      Swal.fire({
        title: 'Verifying Credential...',
        text: 'Cryptographically validating the VC against the issuer...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      const proof = credential.proof || {};
      const finalPublicKey = userPublicKey || proof.creator || "UNKNOWN_PUBLIC_KEY";
      const data = { "credentials": credential };
      
      const config = {
        method: 'post',
        url: `${ssiBaseUrl}/verifier/verify?zpkValidation=false`,
        headers: { 'X-API-KEY': authorizationKey, 'publicKey': finalPublicKey, 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
      };

      const response = await axios.request(config);

      Swal.fire({ icon: "success", title: "Verification Successful", text: "The Verifiable Credential has been cryptographically validated.", confirmButtonColor: "#2ea3f2" });
      return response.data;
    } catch (error) {
      Swal.fire({ icon: "error", title: "Verification Failed", text: "The cryptographic validation failed. Ensure the VC was signed by a trusted issuer.", confirmButtonColor: "#d90429" });
      throw error;
    }
  };

  const handleVerify = () => {
    if (!kycData || !kycData.fileData) return;
    const cleanBase64 = kycData.fileData.includes("base64,") ? kycData.fileData.split("base64,")[1] : kycData.fileData;
    const decodedJson = JSON.parse(atob(cleanBase64));
    verifyIDV(decodedJson, kycData.publicKey);
  };

  return (
    <div className="dashboard-layout">
      <aside className="dashboard-sidebar">
        <div className="sidebar-logo"><div className="logo-icon"></div><h2>CryptoEx</h2></div>
        <nav className="sidebar-nav"><a href="#" className="nav-item active">👤 Identity & KYC</a></nav>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div><h1>Identity Verification Portal</h1><p className="text-muted">Manage Space Wallet KYC requests and incoming webhooks.</p></div>
        </header>

        <section className="content-grid">
          
          <div className="widget-card initiate-widget">
            <div className="widget-header"><h2>Initiate New Request</h2></div>
            <div className="widget-body">
              <div className="form-group">
                <label>Target User ID (UUID)</label>
                <input type="text" value={userId} disabled className="readonly-input" />
              </div>
              {verificationStatus === 'IDLE' ? (
                <button className="primary-button full-width" onClick={handleInitiateKYC}>Generate Magic Link</button>
              ) : (
                <button className="secondary-button full-width" onClick={resetFlow}>Reset Session</button>
              )}
              {magicLink && (
                 <div className="magic-link-container">
                   <div className="link-box"><code>{magicLink.substring(0, 40)}...</code></div>
                   <a href={magicLink} target="_blank" rel="noreferrer" className="action-link">Open Wallet Portal ↗</a>
                 </div>
              )}
            </div>
          </div>

          <div className="widget-card webhook-widget">
            <div className="widget-header">
              <h2>Live Webhook Stream</h2>
              <span className={`status-indicator ${verificationStatus.toLowerCase()}`}>{verificationStatus}</span>
            </div>
            <div className="widget-body flex-center">
              {verificationStatus === 'IDLE' && (
                <div className="empty-state"><div className="empty-icon">📡</div><p>Awaiting session initiation.</p></div>
              )}
              {verificationStatus === 'PENDING' && (
                <div className="polling-state">
                  <div className="loader-spinner"></div>
                  <h4>Listening for Webhook Payload</h4>
                  <p>Tracking ID: <strong>{idvToken}</strong></p>
                </div>
              )}
              {verificationStatus === 'COMPLETED' && kycData && (
                <div className="success-data-state fade-in">
                  <div className="data-header">
                    <div className="avatar-placeholder">✓</div>
                    <div><h3 className="data-name">Credential Received</h3><span className="badge-approved">Ready for Validation</span></div>
                  </div>
                  <div className="data-grid">
                    <div className="data-item" style={{ gridColumn: 'span 2' }}>
                      <label>Document Name</label><div className="data-val font-bold">{kycData.documentName}</div>
                    </div>
                    <div className="data-item">
                      <label>Category</label><div className="data-val">{kycData.category}</div>
                    </div>
                    <div className="data-item">
                      <label>EarthID</label><div className="data-val font-mono">{kycData.userId}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <button onClick={handleViewDocument} style={{ flex: 1, padding: '10px', backgroundColor: '#f1f2fc', border: '1px solid #dce0f5', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', color: '#2ea3f2' }}>
                      View Decrypted Payload
                    </button>
                    <button onClick={handleVerify} style={{ flex: 1, padding: '10px', backgroundColor: '#2ea3f2', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', color: 'white' }}>
                      Verify VC
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="widget-card" style={{ gridColumn: 'span 2' }}>
            <div className="widget-header"><h2>Lookup Previous Verification</h2></div>
            <div className="widget-body" style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label>Tracking ID (idv_token)</label>
                <input type="text" placeholder="e.g. MMARIIER" value={searchToken} onChange={(e) => setSearchToken(e.target.value)} className="readonly-input" style={{ backgroundColor: '#fff', cursor: 'text' }} />
              </div>
              <button className="primary-button" onClick={handleSearchPrevious} disabled={!searchToken.trim()} style={{ height: '42px', padding: '0 24px' }}>Fetch Data</button>
            </div>
          </div>

          {/* --- NEW USER-CENTRIC TRUST PROFILE WIDGET --- */}
          <div className="widget-card" style={{ gridColumn: 'span 2' }}>
            <div className="widget-header">
              <h2>User Trust & Identity Profile</h2>
              <span style={{ 
                padding: '4px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 'bold',
                backgroundColor: userProfile.tier.includes('Verified') ? '#dcfce7' : '#fef3c7',
                color: userProfile.tier.includes('Verified') ? '#166534' : '#92400e'
              }}>
                {userProfile.tier}
              </span>
            </div>
            <div className="widget-body" style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              
              <div style={{ flex: '1', minWidth: '250px', backgroundColor: '#f9fafb', padding: '20px', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                <h4 style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Risk & Activity Metrics</h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #e5e7eb' }}>
                  <span style={{ fontWeight: 600, color: '#374151' }}>Trust Score</span>
                  <span style={{ color: userProfile.trustScore > 80 ? '#10b981' : '#f59e0b', fontWeight: 'bold', fontSize: '1.1rem' }}>{userProfile.trustScore}/100</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontWeight: 500, color: '#4b5563' }}>Linked Wallets</span>
                  <span style={{ fontWeight: 600 }}>{userProfile.linkedWallets}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 500, color: '#4b5563' }}>Last Active</span>
                  <span style={{ fontWeight: 600 }}>{userProfile.lastActive}</span>
                </div>
              </div>

              <div style={{ flex: '2', minWidth: '300px' }}>
                <h4 style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Identity Vault Status</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {userProfile.vault.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: item.status === 'verified' ? '#f0fdf4' : '#fff' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '1.2rem' }}>
                          {item.status === 'verified' ? '✅' : item.status === 'pending' ? '⏳' : '❌'}
                        </span>
                        <span style={{ fontWeight: 600, color: '#374151' }}>{item.type}</span>
                      </div>
                      <span style={{ fontSize: '0.85rem', color: item.status === 'verified' ? '#15803d' : '#6b7280', fontWeight: item.status === 'verified' ? '600' : 'normal' }}>
                        {item.status === 'verified' ? `Verified on ${item.date}` : item.status === 'pending' ? 'Awaiting Wallet...' : 'Action Required'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

        </section>
      </main>
    </div>
  );
};

export default UserVerify;